import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MobileCamera from './MobileCamera';
import KanJegReise from './KanJegReise';
import useBefaringValidation from './useBefaringValidation';
import './MobileBefaring.css';

/**
 * Mobiloptimalisert befaringsflyt
 * Enkel, touch-vennlig navigasjon gjennom befaring
 */
function MobileBefaring() {
  const { eiendomId } = useParams();
  const navigate = useNavigate();
  const eiendom_id = parseInt(eiendomId);
  const [currentStep, setCurrentStep] = useState('loading');
  const [bygningsdeler, setBygningsdeler] = useState([]);
  const [currentBygningsdel, setCurrentBygningsdel] = useState(null);
  const [currentSporsmalIndex, setCurrentSporsmalIndex] = useState(0);
  const [svar, setSvar] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  const [showKanJegReise, setShowKanJegReise] = useState(false);
  const [bilder, setBilder] = useState({});

  // Valideringshook
  const befaringData = {
    bygningsdeler: bygningsdeler.map(del => ({
      ...del,
      svar: svar[del.id] || [],
      bilder: bilder[del.id] || []
    }))
  };
  const validation = useBefaringValidation(befaringData);

  // Hent bygningsdeler og spørsmål
  useEffect(() => {
    fetchBygningsdeler();
  }, [eiendom_id]);

  const fetchBygningsdeler = async () => {
    try {
      const response = await fetch(`/api/eiendom/${eiendom_id}/bygningsdeler`);
      const data = await response.json();

      if (data.bygningsdeler && data.bygningsdeler.length > 0) {
        setBygningsdeler(data.bygningsdeler);
        setCurrentBygningsdel(data.bygningsdeler[0]);
        setCurrentStep('befaring');
      } else {
        setCurrentStep('error');
      }
    } catch (error) {
      console.error('Feil ved henting av bygningsdeler:', error);
      setCurrentStep('error');
    }
  };

  // Neste spørsmål
  const handleNextSporsmal = () => {
    const currentDel = bygningsdeler.find(d => d.id === currentBygningsdel.id);

    if (currentSporsmalIndex < currentDel.sporsmal.length - 1) {
      // Neste spørsmål i samme bygningsdel
      setCurrentSporsmalIndex(currentSporsmalIndex + 1);
    } else {
      // Neste bygningsdel
      const currentDelIndex = bygningsdeler.findIndex(d => d.id === currentBygningsdel.id);

      if (currentDelIndex < bygningsdeler.length - 1) {
        setCurrentBygningsdel(bygningsdeler[currentDelIndex + 1]);
        setCurrentSporsmalIndex(0);
      } else {
        // Ferdig med alle bygningsdeler
        setCurrentStep('summary');
      }
    }
  };

  // Forrige spørsmål
  const handlePreviousSporsmal = () => {
    if (currentSporsmalIndex > 0) {
      setCurrentSporsmalIndex(currentSporsmalIndex - 1);
    } else {
      // Forrige bygningsdel
      const currentDelIndex = bygningsdeler.findIndex(d => d.id === currentBygningsdel.id);

      if (currentDelIndex > 0) {
        const prevDel = bygningsdeler[currentDelIndex - 1];
        setCurrentBygningsdel(prevDel);
        setCurrentSporsmalIndex(prevDel.sporsmal.length - 1);
      }
    }
  };

  // Registrer svar
  const handleAnswerSelect = (sporsmal_id, svar_verdi) => {
    setSvar(prev => ({
      ...prev,
      [currentBygningsdel.id]: [
        ...(prev[currentBygningsdel.id] || []).filter(s => s.sporsmal_id !== sporsmal_id),
        {
          sporsmal_id,
          sporsmal_tekst: currentDel?.sporsmal.find(s => s.id === sporsmal_id)?.sporsmal_tekst,
          svar_verdi
        }
      ]
    }));

    // Auto-advance til neste spørsmål etter 500ms
    setTimeout(() => {
      handleNextSporsmal();
    }, 500);
  };

  // Åpne kamera
  const handleOpenCamera = () => {
    setShowCamera(true);
  };

  // Bilder tatt
  const handlePhotosCaptured = (uploadedImages) => {
    setBilder(prev => ({
      ...prev,
      [currentBygningsdel.id]: [
        ...(prev[currentBygningsdel.id] || []),
        ...uploadedImages
      ]
    }));
    setShowCamera(false);
  };

  // "Kan jeg reise?"
  const handleCheckKanJegReise = () => {
    setShowKanJegReise(true);
  };

  // Fikse feil fra validering
  const handleFixError = (feil) => {
    if (feil.action === 'take_photo') {
      const delToFix = bygningsdeler.find(d => d.id === feil.bygningsdel_id);
      setCurrentBygningsdel(delToFix);
      setShowKanJegReise(false);
      setShowCamera(true);
    } else if (feil.action === 'navigate') {
      const delToFix = bygningsdeler.find(d => d.id === feil.bygningsdel_id);
      setCurrentBygningsdel(delToFix);
      setCurrentSporsmalIndex(0);
      setShowKanJegReise(false);
    }
  };

  // Lagre og fullfør
  const handleComplete = async () => {
    try {
      // Lagre alle svar til backend
      await fetch(`/api/eiendom/${eiendom_id}/befaring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          svar,
          bilder
        })
      });

      // Gå tilbake til eiendom-siden
      navigate(`/eiendom/${eiendom_id}`);
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre befaring. Prøv igjen.');
    }
  };

  // Render basert på step
  if (currentStep === 'loading') {
    return (
      <div className="mobile-befaring loading">
        <div className="loading-spinner">⏳</div>
        <p>Henter bygningsdeler...</p>
      </div>
    );
  }

  if (currentStep === 'error') {
    return (
      <div className="mobile-befaring error">
        <div className="error-icon">❌</div>
        <h2>Feil ved lasting</h2>
        <p>Kunne ikke hente bygningsdeler for eiendom.</p>
        <button className="btn-primary" onClick={() => window.location.reload()}>
          Prøv igjen
        </button>
      </div>
    );
  }

  if (currentStep === 'summary') {
    return (
      <div className="mobile-befaring summary">
        <div className="summary-header">
          <h2>✅ Befaring fullført</h2>
          <p>Gjennomgått {bygningsdeler.length} bygningsdeler</p>
        </div>

        <div className="summary-stats">
          <div className="stat">
            <div className="stat-value">{Object.keys(svar).length}</div>
            <div className="stat-label">Bygningsdeler inspisert</div>
          </div>
          <div className="stat">
            <div className="stat-value">
              {Object.values(bilder).flat().length}
            </div>
            <div className="stat-label">Bilder tatt</div>
          </div>
          <div className="stat">
            <div className="stat-value">{validation.total.kritisk}</div>
            <div className="stat-label">Kritiske mangler</div>
          </div>
        </div>

        <div className="summary-actions">
          <button className="btn-primary large" onClick={handleCheckKanJegReise}>
            🚗 KAN JEG REISE?
          </button>
          <button className="btn-secondary" onClick={() => setCurrentStep('befaring')}>
            Gå tilbake til befaring
          </button>
        </div>
      </div>
    );
  }

  // Hovedflyt - befaring
  const currentDel = bygningsdeler.find(d => d.id === currentBygningsdel?.id);
  const currentSporsmal = currentDel?.sporsmal?.[currentSporsmalIndex];
  const progress = Math.round(
    ((bygningsdeler.findIndex(d => d.id === currentBygningsdel.id) * 100) / bygningsdeler.length) +
    ((currentSporsmalIndex / currentDel?.sporsmal?.length || 1) * (100 / bygningsdeler.length))
  );

  if (!currentDel || !currentSporsmal) {
    return <div className="mobile-befaring loading">Laster...</div>;
  }

  return (
    <div className="mobile-befaring">
      {/* Progress bar */}
      <div className="progress-header">
        <div className="progress-info">
          <span className="progress-text">{progress}% fullført</span>
          <span className="progress-location">{currentDel.bygningsdel_type}</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Spørsmål */}
      <div className="sporsmal-container">
        <div className="sporsmal-header">
          <h3>{currentSporsmal.sporsmal_tekst}</h3>
          {currentSporsmal.hjelpetekst && (
            <p className="hjelpetekst">{currentSporsmal.hjelpetekst}</p>
          )}
        </div>

        {/* Svaralternativer */}
        <div className="svar-alternativer">
          {currentSporsmal.svar_alternativer?.map((alternativ, idx) => (
            <button
              key={idx}
              className="svar-btn"
              onClick={() => handleAnswerSelect(currentSporsmal.id, alternativ)}
            >
              {alternativ}
            </button>
          ))}
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fab-container">
        <button
          className="fab fab-camera"
          onClick={handleOpenCamera}
          title="Ta bilder"
        >
          📸
          {(bilder[currentBygningsdel.id]?.length || 0) > 0 && (
            <span className="fab-badge">{bilder[currentBygningsdel.id].length}</span>
          )}
        </button>

        <button
          className="fab fab-check"
          onClick={handleCheckKanJegReise}
          title="Kan jeg reise?"
        >
          🚗
        </button>
      </div>

      {/* Navigation */}
      <div className="navigation-footer">
        <button
          className="nav-btn nav-prev"
          onClick={handlePreviousSporsmal}
          disabled={
            bygningsdeler.findIndex(d => d.id === currentBygningsdel.id) === 0 &&
            currentSporsmalIndex === 0
          }
        >
          ← Forrige
        </button>

        <button
          className="nav-btn nav-next"
          onClick={handleNextSporsmal}
        >
          Neste →
        </button>
      </div>

      {/* Kamera modal */}
      {showCamera && (
        <MobileCamera
          bygningsdel_id={currentBygningsdel.id}
          minimumPhotos={2}
          currentPhotoCount={bilder[currentBygningsdel.id]?.length || 0}
          onPhotoCaptured={handlePhotosCaptured}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Kan jeg reise modal */}
      {showKanJegReise && (
        <KanJegReise
          validation={validation}
          onClose={() => setShowKanJegReise(false)}
          onFixError={handleFixError}
        />
      )}
    </div>
  );
}

export default MobileBefaring;
